import {
  SenderQualityApplyError,
  type QualityTarget,
  type SenderQualityApplyResult,
} from './types';

interface ApplyAttempt {
  readonly framerate: boolean;
  readonly resolution: boolean;
}

const ATTEMPTS: readonly ApplyAttempt[] = Object.freeze([
  Object.freeze({ framerate: true, resolution: true }),
  Object.freeze({ framerate: true, resolution: false }),
  Object.freeze({ framerate: false, resolution: false }),
]);

/** Applies policy decisions to the native sender without making policy decisions itself. */
export class SenderQualityApplier {
  constructor(private readonly sender: RTCRtpSender) {}

  async apply(target: QualityTarget): Promise<SenderQualityApplyResult> {
    const track = this.sender.track;
    if (!track || track.kind !== 'video') {
      throw new SenderQualityApplyError('quality targets require an attached video track');
    }

    preferScreenDetail(track);
    const scale = calculateScale(track.getSettings(), target);
    let initialError: unknown;

    for (const attempt of ATTEMPTS) {
      try {
        await this.applyAttempt(target, attempt, scale);
        const fields: ('bitrate' | 'framerate' | 'resolution')[] = ['bitrate'];
        if (attempt.framerate) fields.push('framerate');
        if (attempt.resolution && scale !== null) fields.push('resolution');
        return Object.freeze({
          applied: true,
          appliedFields: Object.freeze(fields),
          scaleResolutionDownBy: attempt.resolution ? scale : null,
        });
      } catch (error) {
        initialError ??= error;
      }
    }

    throw new SenderQualityApplyError('the browser rejected all sender quality parameters', {
      cause: initialError,
    });
  }

  private async applyAttempt(
    target: QualityTarget,
    attempt: ApplyAttempt,
    scale: number | null,
  ): Promise<void> {
    const parameters = this.sender.getParameters();
    if (parameters.encodings.length === 0) {
      throw new SenderQualityApplyError('sender has no negotiated encoding parameters');
    }

    for (const encoding of parameters.encodings) {
      encoding.maxBitrate = target.maxBitrateBps;
      if (attempt.framerate) encoding.maxFramerate = target.maxFramesPerSecond;
      if (attempt.resolution && scale !== null) encoding.scaleResolutionDownBy = scale;
    }
    parameters.degradationPreference = 'maintain-resolution';
    await this.sender.setParameters(parameters);
  }
}

function calculateScale(
  settings: MediaTrackSettings,
  target: Pick<QualityTarget, 'maxWidth' | 'maxHeight'>,
): number | null {
  if (!settings.width || !settings.height) return null;
  return Math.max(1, settings.width / target.maxWidth, settings.height / target.maxHeight);
}

function preferScreenDetail(track: MediaStreamTrack): void {
  try {
    track.contentHint = 'detail';
  } catch {
    // contentHint is advisory and is absent or read-only in some implementations.
  }
}
